import * as React from 'react'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import { TbLambda} from "react-icons/tb";
import { MdOutlineCleaningServices } from "react-icons/md";
import { GiConvergenceTarget } from "react-icons/gi";
import Menu from 'material-ui-popup-state/HoverMenu'
import MenuItem from '@mui/material/MenuItem'
import { Link } from "@mui/material";
import PopupState, {
    bindHover,
    bindMenu
} from 'material-ui-popup-state'
import Hidden from "@mui/material/Hidden";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { GoBook } from "react-icons/go";
import { MdOutlineArrowDropDown, MdOutlineArrowRight } from "react-icons/md";
import { styled } from "@mui/material/styles";
import NestedProductMarketingMenuContainer from './NestedProductMarketingMenuContainer';
// Lucide icons for visual consistency
import {
    LuPuzzle,
    LuFlaskConical,
    LuAlignLeft,
    LuChartLine,
    LuChartBarIncreasing,
    LuGlasses,
    LuSquareActivity,
    LuNewspaper,
    LuScanLine,
    LuGauge
} from "react-icons/lu";

const MENU_ITEM_ICON_STYLE = {
    width: '48px',
    minWidth: '48px',
    display: 'flex',
    justifyContent: 'center'
};

const PRODUCT_MARKETING_MENU_CONFIG = {
    triggerLabel: "Use Cases",
    sections: [
        {
            href: "/use-cases/prep-the-data",  // FLAG: was /use-cases/fix-the-data
            icon: LuPuzzle,
            label: "Prep the Data",
            sublabel: "Calibrated agents unify GTM and Finance data. No ETL, no warehouse",
            roles: ["RevOps", "Data", "BI"],
            items: [
                {
                    href: "/products/next-gen-data-assembly",
                    icon: TbLambda,
                    iconProps: { strokeWidth: 2.5 },
                    label: "Data Assembly",
                    sublabel: "ETL-free data assembly that just works"
                },
                {
                    href: "/products/data-hygiene",
                    icon: MdOutlineCleaningServices,
                    label: "Data Hygiene",
                    sublabel: "Hygiene Agents run 24/7, plus tools for one-time cleanup"
                },
                {
                    href: "/products/multiple-crms",
                    icon: GiConvergenceTarget,
                    iconProps: { strokeWidth: 2 },
                    label: "Multi-Entity",
                    sublabel: "Entity Matching Agent: multiple CRMs, ERPs, billing → one source of truth"
                }
            ]
        },
        {
            href: "/use-cases/plan-the-growth",
            icon: LuFlaskConical,
            label: "Plan the Growth",
            sublabel: "Scenario and Deal Eval Agents build projections that show their work",
            roles: ["FP&A", "CFO", "CRO", "CMO"],
            items: [
                {
                    href: "/products/multi-quarter-projections",
                    icon: LuChartBarIncreasing,
                    iconProps: { strokeWidth: 2 },
                    label: "Growth Scenarios",
                    sublabel: "Scenario Agents build rolling 0–36 month forecasts that bring teams together"
                },
                {
                    href: "/products/forecasting",
                    icon: LuGlasses,
                    iconProps: { strokeWidth: 2 },
                    label: "Sales Forecasting",
                    sublabel: "Deal Eval Agents + lightweight forecast submission orchestration"
                },
                {
                    href: "/products/performance-drivers",
                    icon: LuSquareActivity,
                    iconProps: { strokeWidth: 2 },
                    label: "Performance Driver Analysis",
                    sublabel: "On-the-fly driver analysis, explainable and segment-by-segment"
                }
            ]
        },
        {
            href: "/use-cases/run-the-business",
            icon: LuAlignLeft,
            label: "Run the Business",
            sublabel: "QPilot and recipe agents run KPI reviews. Answers, not spreadsheets",
            roles: ["CRO", "CMO", "GTM Leaders"],
            items: [
                {
                    href: "/products/business-digest",
                    icon: LuNewspaper,
                    iconProps: { strokeWidth: 2 },
                    label: "Business Digests",
                    sublabel: "QPilot delivers Monday-morning insights to teams where they work"
                },
                {
                    href: "/products/qpilot",
                    icon: LuScanLine,
                    iconProps: { strokeWidth: 2 },
                    label: "AI Variance Analysis",  // FLAG: route is /products/qpilot — see consistency note
                    sublabel: "Daily agentic variance analysis, every segment, with cited evidence"
                }
            ]
        },
        {
            href: "/use-cases/tell-the-story",
            icon: LuChartLine,
            label: "Tell the Story",
            sublabel: "Board-ready reporting packages from agents that compute, explain, and ship",
            roles: ["CEO", "Board"],
            items: [
                {
                    href: "/products/go-to-market-stats#gtm-reporting-packages",
                    icon: LuChartBarIncreasing,
                    iconProps: { strokeWidth: 2 },
                    label: "GTM Stats",
                    sublabel: "Already-calculated core GTM stats for revenue waterfalls and reporting packages"
                },
                {
                    href: "/products/go-to-market-stats#core-stats",
                    icon: LuGauge,
                    iconProps: { strokeWidth: 2 },
                    label: "Core Efficiency Stats",
                    sublabel: "Rule of 40, NRR, magic number, CAC payback. Already calculated, defensible"
                }
            ]
        }
    ]
};

const ProductMarketingMenu = () => {
    const ProductMarketingMenuItem = styled(MenuItem)(({ theme }) => ({
        backgroundColor: "#FFFFFF",
        padding: "0px",
        textDecoration: "none",
        '&.MuiListItemIcon': {
            minWidth: "10px",
        },
        '&:hover': {
            opacity: 0.84,
        },
        '&visited': {
            textDecoration: "none",
            color: "#272931",
        },
        '&link': {
            textDecoration: "none",
            color: "#272931",
        },
        '&:focus': {},
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
            color: theme.palette.primary.main,
            minWidth: "30px",
            padding: "10px",
            fontWeight: 600,
            fill: theme.palette.primary.main,
            "&:focus": {},
            '&:hover': {
                '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                    color: theme.palette.common.white,
                    backgroundColor: "#393A43",
                }
            }
        }
    }))

    const ProductMarketingMenuItemNested = styled(MenuItem)(({ theme }) => ({
        minWidth: '425px',
        '&.MuiListItemIcon': {
            minWidth: "10px",
        },
        '&:active': {
            backgroundColor: theme.palette.grey[100],
        },
        '&:hover': {
            backgroundColor: theme.palette.grey[100],
        },
        '&:focus': {
            backgroundColor: theme.palette.grey[100],
        },
        '&:selected': {
            backgroundColor: theme.palette.grey[100],
        },
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
            color: theme.palette.primary.main,
            backgroundColor: "transparent",
            fontWeight: 600,
            '&:active': {
                backgroundColor: theme.palette.grey[100],
            },
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
            },
            '&:focus': {
                backgroundColor: theme.palette.grey[100],
            },
            '&:selected': {
                backgroundColor: theme.palette.grey[100],
            },
            '& .MuiButtonBase-root': {
                backgroundColor: theme.palette.grey[100],
                '&:active': {
                    backgroundColor: theme.palette.grey[100],
                },
                '&:selected': {
                    backgroundColor: theme.palette.grey[100],
                },
                '&:hover': {
                    backgroundColor: theme.palette.grey[100],
                },
                '&:focus': {
                    backgroundColor: theme.palette.grey[100],
                }
            }
        }
    }))

    const RolePills = ({ roles }) => (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            marginTop: '6px'
        }}>
            {roles.map((role, index) => (
                <Chip
                    key={index}
                    label={role}
                    size="small"
                    sx={(theme) => ({
                        height: '20px',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        backgroundColor: `${theme.palette.primary.main}10`,
                        color: `${theme.palette.primary.main}dd`,
                        border: 'none',
                        '& .MuiChip-label': {
                            paddingLeft: '8px',
                            paddingRight: '8px'
                        }
                    })}
                />
            ))}
        </div>
    );

    const ParentMenuLabel = ({ icon: Icon, label, sublabel, roles, rightIcon }) => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: '48px 1fr 24px',
            alignItems: 'center',
            width: '100%',
            padding: '0.5em 0',
            boxSizing: 'border-box',
        }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 24 }}>
                <Icon size={24} strokeWidth={2} />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: '1em', color: '#222', lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}> {label} </span>
                <span style={{ fontWeight: 400, fontSize: '0.95em', color: '#666', lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}> {sublabel} </span>
                {roles && <RolePills roles={roles} />}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: 24 }}>
                {rightIcon}
            </span>
        </div>
    );

    return (
        <PopupState variant="popover" popupId="demoMenu">
            {popupState => (
                <React.Fragment>
                    <Button sx={{
                        cursor: "pointer",
                        fontWeight: "bold"
                    }}  {...bindHover(popupState)} endIcon={<MdOutlineArrowDropDown />}>
                        {PRODUCT_MARKETING_MENU_CONFIG.triggerLabel}
                    </Button>
                    <Menu
                        {...bindMenu(popupState)}
                        sx={{
                            list: {
                                padding: "0px"
                            }
                        }}>
                        {PRODUCT_MARKETING_MENU_CONFIG.sections.map((section) => (
                            <NestedProductMarketingMenuContainer
                                key={section.href}
                                rightIcon={null}
                                parentMenuOpen={true}
                                component={Link}
                                href={section.href}
                                label={
                                    <ParentMenuLabel
                                        icon={section.icon}
                                        label={section.label}
                                        sublabel={section.sublabel}
                                        roles={section.roles}
                                        rightIcon={<MdOutlineArrowRight size="1.5em" />}
                                    />
                                }
                            >
                                {section.items.map((item) => (
                                    <ProductMarketingMenuItemNested key={item.href} component={Link} href={item.href}>
                                        <ListItemIcon style={MENU_ITEM_ICON_STYLE}>
                                            <item.icon size={24} {...(item.iconProps || {})} />
                                        </ListItemIcon>
                                        <ListItemText primary={item.label} secondary={item.sublabel} />
                                    </ProductMarketingMenuItemNested>
                                ))}
                            </NestedProductMarketingMenuContainer>
                        ))}

                        <Hidden mdUp>
                            <ProductMarketingMenuItem onClick={popupState.close}>
                                <ListItem style={{ padding: '0px 0px 0px 0px' }} component={Link} href="/login">
                                    <ListItemIcon>
                                        {/* You may want to update this icon to Lucide as well for consistency */}
                                    </ListItemIcon>
                                    <ListItemText primary="Login" />
                                </ListItem>
                            </ProductMarketingMenuItem>

                            <ProductMarketingMenuItem onClick={popupState.close}>
                                <ListItem style={{ padding: '0px 0px 0px 0px' }} component={Link} href="https://blog.qflow.ai">
                                    <ListItemIcon>
                                        <GoBook color="#272931" size="1.65em" />
                                    </ListItemIcon>
                                    <ListItemText primary="Blog" />
                                </ListItem>
                            </ProductMarketingMenuItem>
                        </Hidden>
                    </Menu>
                </React.Fragment>
            )}
        </PopupState>
    )
}

export default ProductMarketingMenu 
